/**
 * Country network: the front-end shape of a country, the adapter from the
 * countries API, the bundled fallback list and the derived counts used in
 * copy (SPEC 5.4, 7). Adapted from the handoff kit's reference/countries.ts.
 *
 * Counts in copy ("22 countries", "21 countries", "19 euro-area countries",
 * "4 coming soon") must always come from `networkCounts`, never be typed.
 */
import type { CountryDto } from "../types/country";
import {
  PAYOUT_LABELS,
  UPCOMING_CORRIDORS,
  UPCOMING_CURRENCY_ORDER,
} from "@/content/corridors";

export type CountryRole = "send" | "receive" | "both";

/** The two payout methods NellsPay offers (SPEC 1). */
export type PayoutMethod = "bank" | "mobile";

/** Shape the front end needs. Map the API onto it only through `toCountry`. */
export interface Country {
  /** ISO 3166-1 alpha-2, upper case: "CA", "CM". */
  code: string;
  /** Display name with typographic punctuation: "Côte d’Ivoire". */
  name: string;
  currencyCode: string;
  currencySymbol: string;
  currencyName: string;
  role: CountryRole;
  /** API region ("Africa", "Europe", ...). Used to place receivers in Africa. */
  region?: string;
  /** Flag URL from the API. The Flag component prefers bundled local files. */
  flagUrl?: string;
  /** Active payout methods reported by the API, when it reports any. */
  payoutMethods?: PayoutMethod[];
  /** API row id, for callers that need to join back to the DTO. */
  apiId?: string;
}

export interface UpcomingCorridor {
  code: string;
  name: string;
  currencyCode: string;
  currencySymbol: string;
}

const EURO_AREA: readonly (readonly [string, string])[] = [
  ["AT", "Austria"], ["BE", "Belgium"], ["CY", "Cyprus"], ["EE", "Estonia"], ["FI", "Finland"],
  ["FR", "France"], ["DE", "Germany"], ["GR", "Greece"], ["IE", "Ireland"], ["IT", "Italy"],
  ["LV", "Latvia"], ["LT", "Lithuania"], ["LU", "Luxembourg"], ["MT", "Malta"], ["NL", "Netherlands"],
  ["PT", "Portugal"], ["SK", "Slovakia"], ["SI", "Slovenia"], ["ES", "Spain"],
];

/**
 * Bundled fallback: what the API returns today, in display order. The store
 * seeds from this so pages (and the prerendered HTML) never render empty.
 */
export const LIVE_COUNTRIES: Country[] = [
  { code: "CA", name: "Canada", currencyCode: "CAD", currencySymbol: "$", currencyName: "Canadian dollar", role: "both", region: "Americas" },
  { code: "GB", name: "United Kingdom", currencyCode: "GBP", currencySymbol: "£", currencyName: "Pound sterling", role: "both", region: "Europe" },
  {
    code: "CM",
    name: "Cameroon",
    currencyCode: "XAF",
    currencySymbol: "Fr",
    currencyName: "Central African CFA franc",
    role: "receive",
    region: "Africa",
    payoutMethods: ["bank", "mobile"],
  },
  ...EURO_AREA.map(([code, name]): Country => ({
    code,
    name,
    currencyCode: "EUR",
    currencySymbol: "€",
    currencyName: "Euro",
    role: "both",
    region: "Europe",
  })),
];

// ── Adapter ─────────────────────────────────────────────────────────────────

type ApiCountry = CountryDto | Record<string, unknown>;

/** Read the first present field, tolerating camelCase and PascalCase payloads. */
function field(api: ApiCountry, ...keys: string[]): unknown {
  const rec = api as Record<string, unknown>;
  for (const k of keys) {
    const v = rec[k] ?? rec[k.charAt(0).toUpperCase() + k.slice(1)];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

function str(v: unknown): string {
  return v === undefined ? "" : String(v).trim();
}

const KNOWN_NAMES = new Map<string, string>(
  [...LIVE_COUNTRIES, ...UPCOMING_CORRIDORS].map((c) => [c.code, c.name]),
);
const KNOWN_SYMBOLS = new Map<string, string>(
  [...LIVE_COUNTRIES, ...UPCOMING_CORRIDORS].map((c) => [c.currencyCode, c.currencySymbol]),
);

/** "Send/Receive" → both, "Receive" → receive, "Send" → send. */
export function parseDirection(direction: unknown): CountryRole {
  const d = str(direction).toLowerCase();
  const send = d.includes("send");
  const receive = d.includes("receive");
  if (send && receive) return "both";
  if (send) return "send";
  // Unknown or "Receive": never list a country as a sender by accident.
  return "receive";
}

function parsePayoutMethods(raw: unknown): PayoutMethod[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out = new Set<PayoutMethod>();
  for (const m of raw) {
    if (!m || typeof m !== "object") continue;
    const status = str(field(m as Record<string, unknown>, "status")).toLowerCase();
    if (status && status !== "active") continue;
    const name = str(field(m as Record<string, unknown>, "name", "description")).toLowerCase();
    // Only bank and mobile money exist on the site (SPEC 1); cash pickup etc. are ignored.
    if (name.includes("bank")) out.add("bank");
    else if (name.includes("mobile")) out.add("mobile");
  }
  if (out.size === 0) return undefined;
  return (["bank", "mobile"] as const).filter((m) => out.has(m));
}

/**
 * Adapter from the countries API row (`CountryDto`) to `Country`.
 * Keeps API naming out of components. Trims padded names ("Austria "), swaps
 * straight apostrophes for typographic ones, and replaces a mangled currency
 * symbol (U+FFFD) with the bundled one.
 */
export function toCountry(api: ApiCountry): Country {
  const code = str(field(api, "country2Code", "countryCode", "code")).toUpperCase();
  const currencyCode = str(field(api, "currencyCode")).toUpperCase();
  const apiName = str(field(api, "countryName", "name")).replace(/'/g, "’");
  const apiSymbol = str(field(api, "currencySymbol"));
  const symbolOk = apiSymbol !== "" && !apiSymbol.includes("�");
  const flag = str(field(api, "countryFlag", "flag"));
  const region = str(field(api, "region"));
  const id = str(field(api, "id"));
  const payoutMethods = parsePayoutMethods(field(api, "paymentMethods"));

  const country: Country = {
    code,
    name: KNOWN_NAMES.get(code) ?? apiName,
    currencyCode,
    currencySymbol: symbolOk ? apiSymbol : (KNOWN_SYMBOLS.get(currencyCode) ?? currencyCode),
    currencyName: str(field(api, "currencyName")),
    role: parseDirection(field(api, "direction")),
  };
  if (region) country.region = region;
  if (flag) country.flagUrl = flag;
  if (payoutMethods) country.payoutMethods = payoutMethods;
  if (id) country.apiId = id;
  return country;
}

/**
 * Map a whole API list: drops inactive rows and rows without a code, and
 * keeps the first row per country code.
 */
export function toCountries(list: readonly ApiCountry[]): Country[] {
  const seen = new Set<string>();
  const out: Country[] = [];
  for (const row of list) {
    const status = str(field(row, "status")).toLowerCase();
    if (status && status !== "active") continue;
    const c = toCountry(row);
    if (!c.code || seen.has(c.code)) continue;
    seen.add(c.code);
    out.push(c);
  }
  return out;
}

// ── Selectors ───────────────────────────────────────────────────────────────

export const senders = (all: readonly Country[]) =>
  all.filter((c) => c.role === "both" || c.role === "send");

const isAfrican = (c: Country) => !c.region || c.region.toLowerCase() === "africa";

/**
 * "Receive across Africa" shows receive-only African markets. Send-and-receive
 * countries are listed under "Send from".
 */
export const africanReceivers = (all: readonly Country[]) =>
  all.filter((c) => c.role === "receive" && isAfrican(c));

/** Countries that can be paid out to (receive or both). */
export const receivers = (all: readonly Country[]) =>
  all.filter((c) => c.role === "receive" || c.role === "both");

/**
 * Upcoming corridors minus any country the API already reports as a live
 * receiver: a corridor that goes live moves from "Coming soon" to "Live"
 * with no code change.
 */
export function activeUpcoming(
  upcoming: readonly UpcomingCorridor[],
  live: readonly Country[],
): UpcomingCorridor[] {
  const liveReceivers = new Set(receivers(live).map((c) => c.code));
  return upcoming.filter((u) => !liveReceivers.has(u.code.toUpperCase()));
}

export interface NetworkCounts {
  /** All live countries: 22. */
  live: number;
  /** Send-from countries: 21. */
  sendFrom: number;
  /** Euro-area send-from countries: 19. */
  euroArea: number;
  /** Live African receive markets: 1. */
  africaLive: number;
  /** Coming-soon African corridors: 4. */
  africaSoon: number;
}

/**
 * Counts used in copy. `upcoming` is filtered against `all` first, so a
 * corridor that goes live is counted once, as live.
 */
export function networkCounts(
  all: readonly Country[],
  upcoming: readonly UpcomingCorridor[] = UPCOMING_CORRIDORS,
): NetworkCounts {
  return {
    live: all.length,
    sendFrom: senders(all).length,
    euroArea: senders(all).filter((c) => c.currencyCode === "EUR").length,
    africaLive: africanReceivers(all).length,
    africaSoon: activeUpcoming(upcoming, all).length,
  };
}

export interface UpcomingCurrency {
  currencyCode: string;
  currencySymbol: string;
  countries: UpcomingCorridor[];
}

/**
 * Coming-soon currencies grouped by currency (XOF covers Senegal and
 * Côte d’Ivoire). Used by the calculator's "Coming soon" group and the
 * Exchange Rates strip. Design order: NGN, GHS, XOF; others follow.
 */
export function upcomingCurrencies(upcoming: readonly UpcomingCorridor[]): UpcomingCurrency[] {
  const byCur = new Map<string, UpcomingCurrency>();
  for (const c of upcoming) {
    const g = byCur.get(c.currencyCode) ?? {
      currencyCode: c.currencyCode,
      currencySymbol: c.currencySymbol,
      countries: [],
    };
    g.countries.push(c);
    byCur.set(c.currencyCode, g);
  }
  const order: readonly string[] = UPCOMING_CURRENCY_ORDER;
  const rank = (code: string) => {
    const i = order.indexOf(code);
    return i === -1 ? order.length : i;
  };
  return [...byCur.values()].sort((a, b) => rank(a.currencyCode) - rank(b.currencyCode));
}

// ── Payout and receive options ──────────────────────────────────────────────

/**
 * Delivery text for a destination. Countries the API reports no payout
 * methods for are bank-deposit destinations (GBP and EUR in the design).
 */
export function payoutText(country: Pick<Country, "payoutMethods">): string {
  const m = country.payoutMethods ?? [];
  const bank = m.includes("bank");
  const mobile = m.includes("mobile");
  if (bank && mobile) return PAYOUT_LABELS.bankAndMobile;
  if (mobile) return PAYOUT_LABELS.mobile;
  return PAYOUT_LABELS.bank;
}

/** One country in a calculator menu (send or receive). */
export interface CountryOption {
  /** Currency code shown on the pill once selected: "XAF". */
  code: string;
  symbol: string;
  countryCode: string;
  /** Shown in the open menu next to the flag. */
  countryName: string;
  /** Delivery line for this destination (receive options only matter). */
  payout: string;
  /** Accessible name, "Cameroon, XAF". */
  label: string;
}

const toOption = (c: Country): CountryOption => ({
  code: c.currencyCode,
  symbol: c.currencySymbol,
  countryCode: c.code,
  countryName: c.name,
  payout: payoutText(c),
  label: `${c.name}, ${c.currencyCode}`,
});

const byName = (a: Country, b: Country) => a.name.localeCompare(b.name, "en-CA");

/** Send menu: every live send-from country, alphabetically. */
export function sendOptions(countries: readonly Country[]): CountryOption[] {
  return senders(countries).slice().sort(byName).map(toOption);
}

/**
 * Receive menu: the live African receiving corridors only (Cameroon today),
 * alphabetically. Send-and-receive countries (the euro area, GB, CA) are
 * send-from markets and never appear here; upcoming corridors are shown
 * separately as disabled "Coming soon" rows.
 *
 * `targets` are the `/fx/corridors` currencies quotable from `sendCurrency`.
 * They narrow the list only when at least one corridor survives: an unknown
 * or incomplete corridor list must never empty the menu, and the quote
 * endpoint remains the arbiter.
 */
export function receiveOptions(
  countries: readonly Country[],
  sendCurrency: string,
  targets: readonly string[] | null | undefined,
): CountryOption[] {
  const send = sendCurrency.toUpperCase();
  const all = africanReceivers(countries).filter((c) => c.currencyCode !== send).sort(byName);
  const allowed = targets && targets.length > 0 ? new Set(targets.map((t) => t.toUpperCase())) : null;
  const priced = allowed ? all.filter((c) => allowed.has(c.currencyCode)) : all;
  return (priced.length > 0 ? priced : all).map(toOption);
}
